// Shared renderer for the EAR/CCL `license_exceptions` field (a deterministic, grounded Part 740
// determination the backend attaches to the settled CCL result). Turns the structured assessment
// into one plain-language block used identically on every surface — the live CCL card, the Export
// results view, the saved-work Consolidated view, and the downloadable PDF — so the field reads the
// same everywhere and SELF-HIDES (returns '') when there is nothing to show (old runs / retrieval
// failure). Model-authored strings (reason, per-exception note) are run through sanitizeModelText.

import { sanitizeModelText } from './sanitizeModelText';

export interface CclLicenseExceptionItem {
  code?: string;
  section?: string | null;
  eligibility?: string;
  note?: string | null;
}

export interface CclLicenseExceptionAssessment {
  eligibility?: 'available' | 'possible' | 'none' | 'not_evaluated' | string;
  available_exceptions?: CclLicenseExceptionItem[];
  reason?: string;
  sections_considered?: string[];
}

const ELIGIBILITY_HEADLINE: Record<string, string> = {
  available:
    'One or more license exceptions may be available (preliminary — verify the conditions).',
  possible: 'A license exception may apply, subject to conditions (preliminary).',
  none: 'No license exception appears available — a license is likely required.',
  // not_evaluated carries its explanation in `reason` (EAR99 / NLR / not assessed) — no headline.
};

const ITEM_ELIGIBILITY: Record<string, string> = {
  likely: 'may be available',
  possible: 'possible — verify conditions',
  restricted: 'restricted / blocked here',
  unavailable: 'not available',
};

/**
 * Format the CCL `license_exceptions` assessment into a single multi-line string for display.
 * Returns '' when there is nothing to show, so every caller can self-hide the field.
 */
export function formatLicenseExceptions(lx: unknown): string {
  if (!lx || typeof lx !== 'object') {
    return '';
  }
  const a = lx as CclLicenseExceptionAssessment;
  const eligibility = String(a.eligibility || '').trim();
  const reason = sanitizeModelText(String(a.reason || '')).trim();
  if (!eligibility && !reason) {
    return '';
  }

  const lines: string[] = [];
  const headline = ELIGIBILITY_HEADLINE[eligibility];
  if (headline) {
    lines.push(headline);
  }
  if (reason) {
    lines.push(reason);
  }

  const items = Array.isArray(a.available_exceptions) ? a.available_exceptions : [];
  const itemLines = items
    .filter((e) => e && e.code)
    .map((e) => {
      const section = e.section ? ` (${String(e.section)})` : '';
      const elig = ITEM_ELIGIBILITY[String(e.eligibility)] || String(e.eligibility || '');
      const note = e.note ? ` — ${sanitizeModelText(String(e.note))}` : '';
      return `• ${e.code}${section}: ${elig}${note}`.trim();
    });
  if (itemLines.length) {
    lines.push('Exceptions considered:');
    lines.push(...itemLines);
  }

  return lines.join('\n').trim();
}

export default formatLicenseExceptions;
